import { useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, useNavigate } from "react-router-dom";

import api from "../../services/api";
import { usePagedList } from "../../hook/usePagedList";
import { useUser } from "../../hook/useUser";
import Pager from "../../components/ui/Pager";
import Modal from "../../components/ui/Modal";
import { notify } from "../../components/ui/Toast";
import { FormError, ModalFoot, PINFL_LENGTH, isPinfl } from "../../components/ui/panelKit";
import { readApiError } from "../../utils/apiError";
import { relativeTime, exactTime } from "../../utils/time";
import { useSearchInput } from "../../hook/useSearchInput";

const PER_PAGE_OPTIONS = [10, 25, 50, 100];
/**
 * A role is not a grade of access within this panel -- it decides WHICH panel
 * the person can open at all. A Mijoz account is refused at this panel's door
 * (oneid_wrong_panel) and signs into the cabinet instead, so the label says
 * where it leads rather than how much it may do.
 */
const ROLE_LABELS = { staff: "Ichki xodim", client: "Mijoz" };

// This form creates staff accounts only -- see the note beside the role field.
const EMPTY = { pinfl: "", full_name: "", email: "", role: "staff" };

/**
 * Logins, one role per page.
 *
 * Staff and cabinet accounts share a table because Django allows exactly one
 * authenticatable model and `pinfl` is unique across both -- one person, one
 * login, enforced by the database. They do not share a screen, because they
 * are not the same job: these are internal colleagues, those are customers,
 * and the two are never managed in the same breath.
 *
 * A login is not a participant. An Identity is somebody writing in a
 * discussion; this is somebody who can open an application. Matched by PINFL
 * and nothing else.
 *
 * `kind` is "staff" or "client" and decides everything that differs: what may
 * be created here, which columns carry meaning, and what an empty list means.
 */
const UsersPage = ({ kind = "staff" }) => {
  const isStaffPage = kind === "staff";
  const { user: me } = useUser();
  const navigate = useNavigate();
  // A row opens the account page: activity, sign-ins and the actions on it.
  const base = isStaffPage ? "/foydalanuvchilar/panel" : "/foydalanuvchilar/kabinet";

  const { input, setInput, search, setSearch } = useSearchInput();
  const [status, setStatus] = useState("");
  const [perPage, setPerPage] = useState(10);

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // The role is the page, not a filter somebody can widen.
  const params = useMemo(
    () => ({ role: kind, ...(search ? { q: search } : {}), ...(status ? { status } : {}) }),
    [kind, search, status],
  );

  const { rows, count, loading, error, page, setPage, totalPages, reload } =
    usePagedList("/users/", params, perPage);

  const filtering = Boolean(search || status);
  const from = count === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, count);

  const save = async () => {
    if (!isPinfl(form.pinfl)) {
      setFormError(`PINFL roppa-rosa ${PINFL_LENGTH} ta raqamdan iborat boʻlishi kerak.`);
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      await api.post("/users/", {
        pinfl: form.pinfl.trim(),
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        role: form.role,
      });
      setForm(null);
      notify.success("Hisob qoʻshildi. U OneID orqali birinchi kirishda faollashadi.");
      reload();
    } catch (e) {
      setFormError(readApiError(e, {}, "Hisobni qoʻshib boʻlmadi."));
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="ms-panel">
      <form
        className="ms-toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(input.trim());
        }}
      >
        <div className="ms-search">
          <Icon icon="tabler:search" />
          <input
            type="text"
            placeholder="PINFL, ism yoki pochta..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            aria-label="Qidiruv"
          />
        </div>

        <select className="ms-select ms-select--sm" value={status} aria-label="Holati"
                onChange={(e) => setStatus(e.target.value)}>
          <option value="">Barcha holatlar</option>
          <option value="active">Faol</option>
          <option value="off">Oʻchirilgan</option>
        </select>

        {filtering && (
          <button type="button" className="ms-btn ms-btn--ghost"
                  onClick={() => { setInput(""); setSearch(""); setStatus(""); }}>
            <Icon icon="tabler:x" />
            Tozalash
          </button>
        )}

        <div className="ms-toolbar-spacer" />

        {/* Only on the staff page. A cabinet login is created on the client's
            own page, where the login and its grant are made together -- one
            made here would have no client and its owner would sign in to
            nothing. */}
        {isStaffPage && (
          <button type="button" className="ms-btn ms-btn--primary"
                  onClick={() => { setForm({ ...EMPTY }); setFormError(""); }}>
            <Icon icon="tabler:user-plus" />
            Yangi hisob
          </button>
        )}
      </form>

      {loading ? (
        <div className="ms-state"><div className="ms-spinner" /></div>
      ) : error ? (
        <div className="ms-state">
          <Icon icon="tabler:alert-triangle" />
          <span>{error}</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="ms-state">
          <Icon icon="tabler:user-off" />
          <span>
            {filtering
              ? "Hech narsa topilmadi"
              : isStaffPage
                ? "Ichki xodim hisobi yoʻq"
                : "Kabinet hisobi yoʻq — ular mijoz tizimi sahifasida ochiladi"}
          </span>
        </div>
      ) : (
        <>
          <div className="ms-table-wrap">
            <table className="ms-table ms-table--compact">
              <thead>
                <tr>
                  <th>Hisob</th>
                  <th style={{ width: 220 }}>Elektron pochta</th>
                  {isStaffPage ? (
                    <th style={{ width: 170 }}>Qoʻshgan</th>
                  ) : (
                    <th style={{ width: 240 }}>Mijoz tizimlari</th>
                  )}
                  <th style={{ width: 120 }}>Holati</th>
                  <th style={{ width: 140 }}>Oxirgi kirish</th>
                  <th style={{ width: 140 }}>Oxirgi amal</th>
                  <th style={{ width: 44 }} aria-label="Ochish" />
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => {
                  const self = me && u.id === me.id;
                  const to = `${base}/${u.id}`;
                  const open = () => navigate(to);
                  return (
                    <tr
                      key={u.id}
                      className="is-clickable"
                      tabIndex={0}
                      onClick={open}
                      onKeyDown={(e) => e.key === "Enter" && open()}
                    >
                      <td>
                        <div className="ms-namecell">
                          <Link to={to} className="ms-sublink" onClick={(e) => e.stopPropagation()}>
                            <b>{u.full_name || "Ism hali kelmagan"}</b>
                            {self && (
                              <span className="ms-badge ms-badge--info" style={{ marginInlineStart: 8 }}>
                                siz
                              </span>
                            )}
                          </Link>
                          <span>{u.pinfl || "—"}</span>
                        </div>
                      </td>
                      <td className="ms-td-muted ms-td-clamp">{u.email || "—"}</td>
                      {isStaffPage ? (
                        <td className="ms-td-muted ms-td-clamp">{u.created_by || "—"}</td>
                      ) : (
                        <td>
                          {/* No grant means this person can sign in and see
                              nothing, which is the state worth spotting. */}
                          {u.clients?.length ? (
                            <div className="ms-cell-stack">
                              {u.clients.map((c) => (
                                <span key={c.id}>
                                  {c.name}{" "}
                                  <span className="ms-field-hint">
                                    {c.access === "admin" ? "administrator" : "oʻqish"}
                                  </span>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="ms-badge ms-badge--progress">ruxsat yoʻq</span>
                          )}
                        </td>
                      )}
                      <td>
                        <span className={`ms-badge ms-badge--${u.is_active ? "success" : "danger"}`}>
                          {u.is_active ? "faol" : "oʻchirilgan"}
                        </span>
                      </td>
                      <td className="ms-td-muted" title={exactTime(u.last_oneid_login_at)}>
                        {u.last_oneid_login_at ? relativeTime(u.last_oneid_login_at) : "hali kirmagan"}
                      </td>
                      <td className="ms-td-muted" title={exactTime(u.last_action_at)}>
                        {u.last_action_at ? relativeTime(u.last_action_at) : "—"}
                      </td>
                      <td>
                        <span className="ms-rowgo">
                          <Icon icon="tabler:chevron-right" />
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="ms-tablefoot">
            <span className="ms-tablefoot-count">
              Koʻrsatilmoqda <b>{from}</b>—<b>{to}</b> / jami <b>{count}</b> ta
            </span>
            <div className="ms-tablefoot-right">
              <label className="ms-perpage">
                <span>Koʻrsatish</span>
                <select className="ms-select ms-select--sm" value={perPage}
                        onChange={(e) => setPerPage(Number(e.target.value))}>
                  {PER_PAGE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <Pager page={page} totalPages={totalPages} onPage={setPage} />
            </div>
          </div>
        </>
      )}

      <Modal
        open={Boolean(form)}
        title="Yangi hisob"
        onClose={() => setForm(null)}
        footer={
          <ModalFoot onCancel={() => setForm(null)} onSave={save} saving={saving}
                     saveLabel="Qoʻshish" />
        }
      >
        {form && (
          <>
            <FormError>{formError}</FormError>

            <div className="ms-field">
              <label htmlFor="u-pinfl">PINFL</label>
              <input
                id="u-pinfl"
                className="ms-mono"
                inputMode="numeric"
                autoComplete="off"
                placeholder="14 ta raqam"
                value={form.pinfl}
                // Digits only: OneID matches on this exact string, and a
                // stray space or dash makes the account unreachable by the
                // person it was created for.
                onChange={(e) =>
                  setForm({ ...form, pinfl: e.target.value.replace(/\D/g, "").slice(0, PINFL_LENGTH) })
                }
              />
              <span className="ms-field-hint">
                {form.pinfl.length}/{PINFL_LENGTH}
              </span>
            </div>

            <div className="ms-field">
              <label htmlFor="u-name">Toʻliq ism</label>
              <input id="u-name" value={form.full_name}
                     onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            </div>

            <div className="ms-field">
              <label htmlFor="u-email">Elektron pochta</label>
              <input id="u-email" type="email" value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <span className="ms-field-hint">
                Ixtiyoriy. OneID pochta qaytarmaydi — bu faqat roʻyxatda tanish uchun belgi.
              </span>
            </div>

            {/* Staff only, and not a dropdown: a Mijoz account created here
                would have no client attached, so its owner could sign into the
                cabinet and see nothing. Customer logins are created on the
                client's own page, where the login and the grant are made
                together and the panel can say which it did. */}
            <div className="ms-field">
              <label>Rol</label>
              <div className="ms-static">
                <span className="ms-badge ms-badge--info">{ROLE_LABELS.staff}</span>
              </div>
              <span className="ms-field-hint">
                Bu yerda faqat ichki xodim hisobi ochiladi — u boshqaruv paneliga
                kiradi va barcha mijoz tizimlarini koʻradi. Mijoz hisobi esa
                mijoz tizimi sahifasidagi «Kabinet menejerlari» boʻlimida
                ochiladi: u yerda hisob ham, ruxsat ham bir vaqtda beriladi.
              </span>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default UsersPage;
